import { useEffect, useRef, useState } from 'react';
import type { ShouldRevalidateFunction } from 'react-router';
import type { AffiliateStockListing, CapiProductPage } from 'types/capi/pdp';
import type { Price as PriceType } from 'types/capi/shared';

import { ExternalLink } from '~/components/Button';
import PageLoadErrorBoundary from '~/components/PageLoadErrorBoundary';
import Price from '~/components/Price';
import SoleMarketUsp from '~/components/SoleMarketUsp';
import PdpViewEvent from '~/events/PdpViewEvent';
import usePageData from '~/hooks/usePageData';
import usePageViewEvent from '~/hooks/usePageViewEvent';
import useQsParams from '~/hooks/useQsParams';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';
import gtm from '~/services/gtm.client';
import { getPriceForSize, getStockStatusForSize } from '~/utils/listings';
import { formatPrice } from '~/utils/prices';
import seo from '~/utils/seo';

import StickyCta from '../affiliate/WhereToBuyInStock/StickyCta';
import { StockListingButtons } from '../affiliate/WhereToBuyInStock/StockListing';
import { Carousel } from '../components/Carousel';
import ProductDetailsTruncated from '../components/ProductDetails/ProductDetailsTruncated';
import ProductMetaData from '../components/ProductMetaData';
import SocialSection from '../components/SocialSection';
import SizeSelection from '../components/WhereToBuy/SizeSelection';
import RedirectBanner from './RedirectBanner';

export default function ProductPage() {
  const user = useUser();
  const product = usePageData<CapiProductPage>();
  const { clientIp, isBot } = useRootData();

  useStructuredData(product._schemas);
  usePageViewEvent('pdp');

  useEffect(() => {
    new PdpViewEvent({
      clientIp,
      isBot,
      product,
      user
    }).send();
  }, [product, clientIp, user, isBot]);

  return (
    <div key={product.product_id} className="content-width mt-2 pt-4">
      <div className="flex flex-col gap-8 pb-12">
        <div className="grid md:grid-cols-6 lg:grid-cols-12 lg:gap-6">
          <div className="flex flex-col gap-4 md:col-span-6 lg:col-span-8 xl:col-span-8">
            {product.redirected_retailer_name && (
              <RedirectBanner
                redirectedRetailerName={product.redirected_retailer_name}
              />
            )}
            <Carousel />
            <div className="my-4 md:mt-10">
              <ProductMetaData />
            </div>
            <ProductDetailsTruncated showEditorialPolicy={false} />
          </div>
          <WhereToBuy product={product} />
        </div>
        <SocialSection />
      </div>
    </div>
  );
}

function WhereToBuy({ product }: { product: CapiProductPage }) {
  const [selectedSize, setSelectedSize] = useState(useQsParams().size);
  const [isSizeGridExpanded, setIsSizeGridExpanded] = useState(true);

  const sizeSelectorRef = useRef<HTMLDivElement>(null);

  function onCtaSizeSelectorClick() {
    sizeSelectorRef.current?.scrollIntoView({ behavior: 'smooth' });
    setIsSizeGridExpanded(true);
    gtm.sendUserInteractionEvent('select size', 'Sticky CTA');
  }

  const listing = product.stock_listings.affiliates[0]!;
  const stockStatus = getStockStatusForSize(listing, selectedSize);
  const priceForSize = getPriceForSize(listing, selectedSize) ?? listing.price;

  const buyNowUrl = `${listing.url_template}${
    selectedSize && selectedSize !== '6' ? `/sizes/${selectedSize}` : ''
  }`;

  return (
    <div className="w-full md:col-span-full lg:col-span-4">
      <div className="mb-4 flex flex-col gap-6 rounded-xl bg-white">
        <div ref={sizeSelectorRef}>
          <SizeSelection
            shouldShowSizeGrid={true}
            selectedSize={selectedSize}
            setSelectedSize={setSelectedSize}
            isExpanded={isSizeGridExpanded}
            setIsExpanded={setIsSizeGridExpanded}
            sizes={listing.sizes}
            forceExpandedSizeGrid={true}
          />
        </div>
        <StockListing
          price={priceForSize}
          listing={listing}
          selectedSize={selectedSize}
          stockStatus={stockStatus}
          buyNowUrl={buyNowUrl}
        />
      </div>
      <SoleMarketUsp />
      <StickyCta
        hideOnElementRef={sizeSelectorRef}
        selectedSize={selectedSize}
        stockStatus={stockStatus}
        onSizeSelectorClick={onCtaSizeSelectorClick}
      >
        <StockListingButtons
          stockStatus={stockStatus}
          selectedSize={selectedSize}
          listing={listing}
          position={0}
          pagePosition="Sticky CTA"
          onBuyNowClick={() => {}}
          showAfffiliateLogo={true}
          dynamicVariant
        />
      </StickyCta>
    </div>
  );
}

function StockListing({
  price,
  listing,
  buyNowUrl,
  selectedSize,
  stockStatus
}: {
  buyNowUrl: string;
  listing: AffiliateStockListing;
  price: PriceType;
  selectedSize?: string;
  stockStatus: string;
}) {
  return (
    <div className="flex w-full flex-col gap-4 p-4">
      <div className="flex grow items-center justify-between gap-4">
        <div className="flex gap-4">
          <div className="h-9 w-16">
            <img
              src={listing.affiliate.logo}
              alt={listing.affiliate.name}
              className="rounded bg-white object-cover text-black"
            />
          </div>
          {stockStatus === 'SOLD_OUT' && (
            <div className="text-sm text-red-alert md:text-base">
              <p>Out of stock</p>
              <p className="text-black">UK {selectedSize}</p>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end justify-center">
          <StockListingPrice price={price} />
          <p className="text-right text-sm font-normal text-grey-hide">
            incl. delivery & fees
          </p>
        </div>
      </div>
      <BuyNowButton stockStatus={stockStatus} url={buyNowUrl} />
    </div>
  );
}

function StockListingPrice({ price }: { price: PriceType }) {
  return (
    <div className="flex gap-1 text-base">
      From:
      <Price
        regular={price.regular}
        currency={price.currency}
        className="flex-col gap-0 text-base font-bold"
      />
    </div>
  );
}

function BuyNowButton({
  url,
  stockStatus,
  price
}: {
  buttonRef?: React.RefObject<HTMLDivElement>;
  price?: PriceType;
  stockStatus: string;
  url: string;
}) {
  const isSoldOut = stockStatus === 'SOLD_OUT';
  const buyNowText = `Buy now ${
    price?.regular ? `from ${formatPrice(price.regular, price.currency)}` : ''
  }`;

  return (
    <ExternalLink href={url} variant={isSoldOut ? 'disabled' : 'forest'}>
      <div className="flex h-8 items-center justify-center gap-2">
        {isSoldOut ? 'Sold out' : buyNowText}
      </div>
    </ExternalLink>
  );
}

export function ErrorBoundary() {
  return <PageLoadErrorBoundary pageType="pdp" />;
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction
}) => {
  if (formAction?.startsWith('/bolt')) return false;

  return defaultShouldRevalidate;
};

type LoaderData = {
  pageData: CapiProductPage;
};

export const meta = ({ data }: { data?: LoaderData }) => {
  const meta = data
    ? seo.formatForMeta(data.pageData.canonical_url, data.pageData.seo)
    : [];

  return seo.addMetaNoIndex(meta);
};
export { loader } from './loader.server';
